// engine-exception: the ops board is a decision surface, not a list — three aggregate reads (ops totals + at-risk tickets, the experience score, capacity) composed into one page the manager watches all day; no resource list expresses it
import { Link } from 'react-router-dom';
import { useApiQuery } from '../../engine/api/hooks';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton, Stat } from '../../engine/design/primitives/Misc';
import { formatMinutes } from '../../lib/format';

// The live ops board (docs/servicedesk-v2-proposal.md P12): what will breach in the next two hours,
// what nobody owns, who is waiting on us, and whether the desk has the hours to do it. Next to the
// experience score, because SLA attainment on its own is a number you can hit while the customer
// quietly gives up on you.

interface Totals {
  open_tickets: number; unassigned: number; awaiting_first_reply: number;
  waiting_on_customer: number; waiting_on_us: number; older_than_a_week: number;
  breached: number; due_within_2h: number; due_today: number;
}
interface AtRisk { id: string; ref: string | null; summary: string | null; due_at: string; breached: boolean; assigned_tech_id: string | null }
interface QueueHealth { queue_id: string; name: string; open_tickets: number; unassigned: number; awaiting_first_reply: number }
interface Ops { totals: Totals; at_risk: AtRisk[]; queues: QueueHealth[] }
interface Component { key: string; label: string; score: number | null; detail: string }
interface Xla { overall: { tickets: number; score: number | null; components: Component[] }; breakdown: Array<{ id: string | null; tickets: number; score: number | null }> }
interface Capacity { technician_id: string; name: string; capacity_minutes: number; billable_minutes: number; open_tickets: number; committed_minutes: number; utilisation_pct: number | null; target_pct: number | null; verdict: string | null }

const scoreTone = (n: number | null) => (n === null ? 'muted' : n >= 80 ? 'pos' : n >= 60 ? 'warn' : 'neg');
const dueIn = (iso: string) => {
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  return mins < 0 ? `${formatMinutes(-mins)} late` : `in ${formatMinutes(mins)}`;
};

export function OpsBoardScreen() {
  // 30 seconds: this is a screen people leave open, and a stale breach count is worse than none.
  const ops = useApiQuery<Ops>(['ops-board'], '/v1/tickets/ops', { staleTime: 30_000 });
  const xla = useApiQuery<Xla>(['ops-xla'], '/v1/tickets/xla?days=30&by=client');
  const capacity = useApiQuery<{ data: Capacity[] }>(['ops-capacity'], '/v1/technicians/capacity?days=7');

  if (ops.error) return <ErrorBanner error={ops.error} />;
  if (!ops.data) return <Skeleton lines={8} />;
  const t = ops.data.totals;

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      <PageHeader
        title="Live ops"
        subtitle="What will breach, what nobody owns, and whether we have the hours."
        actions={xla.data ? <Badge tone={scoreTone(xla.data.overall.score)}>Experience {xla.data.overall.score ?? '—'}</Badge> : undefined}
      />

      <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
        <Stat label="Breached" value={String(t.breached)} accent={t.breached > 0} />
        <Stat label="Due within 2h" value={String(t.due_within_2h)} />
        <Stat label="Due today" value={String(t.due_today)} />
        <Stat label="Nobody owns" value={String(t.unassigned)} />
        <Stat label="No reply yet" value={String(t.awaiting_first_reply)} />
        <Stat label="Waiting on us" value={String(t.waiting_on_us)} />
        <Stat label="Waiting on them" value={String(t.waiting_on_customer)} />
        <Stat label="Older than a week" value={String(t.older_than_a_week)} />
      </div>

      <section aria-label="At risk">
        <h2 className="hf-section-title">At risk</h2>
        {ops.data.at_risk.length === 0 ? (
          <EmptyState title="Nothing about to breach" hint="The next two hours are clear." icon="check-circle" />
        ) : (
          <ul className="hf-stack" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0 }}>
            {ops.data.at_risk.map((r) => (
              <li key={r.id} className="hf-row" style={{ gap: 'var(--s-1)', fontSize: 'var(--t-12)' }}>
                <Badge tone={r.breached ? 'neg' : 'warn'}>{dueIn(r.due_at)}</Badge>
                <Link className="hf-link" to={`/tickets/${r.id}`}>{r.ref ?? r.id.slice(0, 8)}</Link>
                <span>{r.summary}</span>
                {!r.assigned_tech_id && <Badge tone="neg">unassigned</Badge>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Queues">
        <h2 className="hf-section-title">Queues</h2>
        <table className="hf-table">
          <thead><tr><th>Queue</th><th className="hf-num">Open</th><th className="hf-num">Unassigned</th><th className="hf-num">No reply yet</th></tr></thead>
          <tbody>
            {ops.data.queues.map((q) => (
              <tr key={q.queue_id}>
                <td>{q.name}</td>
                <td className="hf-num">{q.open_tickets}</td>
                <td className="hf-num">{q.unassigned}</td>
                <td className="hf-num">{q.awaiting_first_reply}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {xla.data && (
        <section aria-label="Experience">
          <h2 className="hf-section-title">Experience, last 30 days</h2>
          <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
            {xla.data.overall.components.map((c) => (
              // A component with no data says so rather than scoring zero — a young client is not a bad one.
              <Stat key={c.key} label={c.label} value={c.score === null ? '—' : String(c.score)} sub={c.detail} />
            ))}
          </div>
        </section>
      )}

      {capacity.data && capacity.data.data.length > 0 && (
        <section aria-label="Capacity">
          <h2 className="hf-section-title">Capacity, next 7 days</h2>
          <table className="hf-table">
            <thead><tr><th>Technician</th><th className="hf-num">Billable</th><th className="hf-num">Capacity</th><th className="hf-num">Booked</th><th className="hf-num">Open</th><th>Utilisation</th></tr></thead>
            <tbody>
              {capacity.data.data.map((c) => (
                <tr key={c.technician_id}>
                  <td>{c.name}</td>
                  <td className="hf-num">{formatMinutes(c.billable_minutes)}</td>
                  <td className="hf-num">{formatMinutes(c.capacity_minutes)}</td>
                  <td className="hf-num">{formatMinutes(c.committed_minutes)}</td>
                  <td className="hf-num">{c.open_tickets}</td>
                  <td>
                    {c.utilisation_pct === null ? '—' : `${c.utilisation_pct}%`}
                    {/* Only judged where the tenant actually set a target. */}
                    {c.verdict && <Badge tone={c.verdict === 'on target' ? 'pos' : c.verdict === 'over' ? 'warn' : 'muted'}>{c.verdict}</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
